import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState,
	ReactNode,
} from "react";
import {
	getText,
	Lang,
	persistLang,
	readStoredLang,
	Text,
} from "./i18n";

type LanguageContextValue = {
	lang: Lang;
	text: Text;
	setLang: (lang: Lang) => void;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
	const [lang, setLangState] = useState<Lang>(() =>
		typeof window === "undefined" ? "en" : readStoredLang(),
	);

	useEffect(() => {
		document.documentElement.lang = lang;
	}, [lang]);

	const value = useMemo(
		() => ({
			lang,
			text: getText(lang),
			setLang: (next: Lang) => {
				setLangState(next);
				persistLang(next);
			},
		}),
		[lang],
	);

	return (
		<LanguageContext.Provider value={value}>
			{children}
		</LanguageContext.Provider>
	);
}

export function useLanguage() {
	const ctx = useContext(LanguageContext);
	if (!ctx) {
		throw new Error("useLanguage must be used inside LanguageProvider");
	}
	return ctx;
}
